import React from 'react';
import { Avatar, Grid, Paper, Stack, Typography, Divider } from '@mui/material';
import Section from '../shared/Section';
import SectionHeading from '../shared/SectionHeading';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Diversity3RoundedIcon from '@mui/icons-material/Diversity3Rounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

const missionHighlights = [
  {
    title: 'Plan with Precision',
    stat: '30% prep waste avoided',
    description:
      'Our platform tells your chefs exactly what to cook. They focus on culinary creativity; we handle the math to stop waste before it starts.',
    Icon: GroupsRoundedIcon,
    gradient: '#FFFFFF',
    tailGradient: 'linear-gradient(90deg, rgba(47,58,114,0.9) 0%, rgba(47,58,114,0.6) 100%)',
    accentColor: '#2F3A72',
  },
  {
    title: 'Fresh Meals, Made Daily',
    stat: '100% fresh ingredients',
    description:
      'Our kitchen partners prepare meals fresh every day. No processed food, no shortcuts— just healthy, freshly cooked meals made for everyday eating.',
    Icon: Diversity3RoundedIcon,
    gradient: '#FFFFFF',
    tailGradient: 'linear-gradient(90deg, rgba(246,120,95,0.9) 0%, rgba(246,120,95,0.6) 100%)',
    accentColor: '#F6785F',
  },
  {
    title: 'Measure Your Wins',
    stat: 'Live sustainability dashboards',
    description:
      'Track your carbon footprint and cost savings in one click. Prove your ESG impact to the board without the spreadsheet headache.',
    Icon: PublicRoundedIcon,
    gradient: '#FFFFFF',
    tailGradient: 'linear-gradient(90deg, rgba(46,189,133,0.9) 0%, rgba(46,189,133,0.6) 100%)',
    accentColor: '#2EBD85',
  },
];

const aboutDescription =
  'FoodFe is the Operating System for the modern canteen. We sync your hungry team with our catering partners, ensuring every meal is delicious and nothing goes to the bin.';

export const About = () => {
  return (
    <Section
      id='about'
      background='linear-gradient(180deg, #FFFFFF 0%, rgba(245, 247, 251, 0.8) 100%)'
    >
      <Stack spacing={8}>
        <SectionHeading
          eyebrow='About FoodFe'
          title='Tech that serves the planet. Food that serves your people.'
          description={aboutDescription}
        />
        <Grid
          container
          rowSpacing={4}
          columnSpacing={{ xs: 0, md: 4 }}
          sx={{
            justifyContent: { xs: 'center', md: 'flex-start' },
            maxWidth: { xs: 420, md: 'none' },
            mx: { xs: 'auto', md: 0 },
          }}
        >
          {missionHighlights.map((item) => (
            <Grid
              item
              xs={12}
              md={4}
              key={item.title}
              sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}
            >
              <Paper
                elevation={0}
                sx={{
                  position: 'relative',
                  overflow: 'visible',
                  width: { xs: 'min(420px, 100%)', md: '100%' },
                  mx: 'auto',
                  height: '100%',
                  p: { xs: 4, md: 5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  borderRadius: 18,
                  border: '1px solid rgba(47, 58, 114, 0.08)',
                  background: item.gradient,
                  boxShadow: 'none',
                  color: 'text.primary',
                  pb: { xs: 7, md: 8 },
                  transition: 'transform 200ms ease, box-shadow 220ms ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0px 28px 50px rgba(31, 36, 51, 0.16)',
                    '&::after': {
                      transform: 'translateX(-50%) translateY(4px)',
                      filter: 'blur(2px)',
                    },
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    bottom: -24,
                    transform: 'translateX(-50%)',
                    width: '68%',
                    maxWidth: 220,
                    height: 24,
                    background: item.tailGradient,
                    clipPath: 'polygon(0 0, 100% 0, calc(100% - 28px) 100%, 28px 100%)',
                    borderRadius: '0 0 12px 12px',
                    boxShadow: 'none',
                    transition: 'transform 200ms ease, filter 200ms ease',
                  },
                }}
              >
                <Stack
                  spacing={2}
                  sx={{ position: 'relative', alignItems: 'center', textAlign: 'center' }}
                >
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      backgroundColor: item.accentColor,
                      color: '#FFFFFF',
                    }}
                  >
                    <item.Icon />
                  </Avatar>
                  <Stack spacing={0.5} alignItems='center'>
                    <Typography
                      variant='bodySmallCaps'
                      sx={{ color: item.accentColor }}
                    >
                      {item.accent}
                    </Typography>
                    <Typography variant='h4' sx={{ lineHeight: 1.26 }}>
                      {item.title}
                    </Typography>
                  </Stack>
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{
                      lineHeight: 1.7,
                      fontSize: { xs: '1.5rem', md: '1.6rem' },
                    }}
                  >
                    {item.description}
                  </Typography>
                </Stack>
                <Divider
                  sx={{
                    my: 1.5,
                    mx: 'auto',
                    width: '70%',
                    borderColor: 'rgba(47, 58, 114, 0.15)',
                  }}
                />
                <Typography
                  variant='subtitle2'
                  sx={{
                    position: 'relative',
                    fontWeight: 600,
                    textAlign: 'center',
                    display: 'block',
                    color: item.accentColor,
                  }}
                >
                  {item.stat}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Section>
  );
};
